import React, { useEffect, useState } from 'react';
import { Alert, Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import GenericTextField from '../../../../components/GenericTextField/GenericTextField';
import { MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import { IssueOutcomeEnum } from '../../../../common/enums/invoicing.enum';
import { InvoiceDto, IssueInvoicesResultDto } from '../../../../common/interfaces/invoicing.interface';
import { getBillingSettings, issueInvoices } from '../../../../api/invoicing.api';
import { createNotification } from '../../../../utilities/helper/notification.helper';
import { toLocalDateString } from '../../../../utilities/helper/date.helper';

interface IssueInvoicesDialogProps {
  kindergartenId: string;
  /** The drafts to issue; anything else in the list is ignored by the server. */
  drafts: InvoiceDto[];
  /** "za oktobar 2026"; left out when issuing from a selection or one draft. */
  monthLabel?: string;
  onClose: () => void;
  onDone: (result: IssueInvoicesResultDto) => void;
}

const addDays = (date: string, days: number) => {
  const d = new Date(`${date}T12:00:00`);
  d.setDate(d.getDate() + days);
  return toLocalDateString(d);
};

/**
 * Gives drafts their numbers - one, a selection, or all of a month's - and can send them
 * to the parents straight away.
 *
 * The due date is only sent when it was changed here. Left alone, each draft keeps the
 * due date saved on it, and those without one get the kindergarten's default; a dialog
 * that always sent its own date would quietly overwrite what was set on a draft.
 */
const IssueInvoicesDialog: React.FC<IssueInvoicesDialogProps> = ({ kindergartenId, drafts, monthLabel, onClose, onDone }) => {
  const { t } = useTranslation();
  const today = toLocalDateString(new Date());
  const [issueDate, setIssueDate] = useState(today);
  const [dueDays, setDueDays] = useState<number | null>(null);
  const [dueDate, setDueDate] = useState('');
  const [dueTouched, setDueTouched] = useState(false);
  const [send, setSend] = useState(true);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState<IssueInvoicesResultDto | null>(null);

  useEffect(() => {
    getBillingSettings(kindergartenId)
      .then(result => setDueDays(result.results?.defaultPaymentDueDays ?? null))
      .catch(() => undefined);
  }, [kindergartenId]);

  // the default follows the issue date until the admin picks a date of their own
  useEffect(() => {
    if (!dueTouched && dueDays !== null) setDueDate(addDays(issueDate, dueDays));
  }, [issueDate, dueDays, dueTouched]);

  const withEmail = drafts.filter(draft => !!draft.payerEmail).length;
  // issued as paid, without a QR code: there is nothing to pay
  const zero = drafts.filter(draft => draft.totalGross <= 0).length;

  const run = async () => {
    setRunning(true);
    try {
      const result = await issueInvoices(kindergartenId, {
        invoiceIds: drafts.map(draft => draft.id),
        issueDate,
        dueDate: dueTouched && dueDate ? dueDate : undefined,
        sendToPayer: send
      });
      if (result.results) {
        onDone(result.results);
        // a recount changed what some invoices charge, so the admin sees it before closing
        const recounted = result.results.outcomes.some(outcome => !!outcome.attendanceRecount);
        if (result.results.failedCount || recounted) {
          setReport(result.results);
        } else {
          createNotification(
            MESSAGE_TYPE.SUCCESS,
            send
              ? t('invoices.issueDialog.issuedAndQueued', { count: result.results.issuedCount, queued: result.results.queuedForSendingCount })
              : t('invoices.issueDialog.issued', { count: result.results.issuedCount })
          );
          onClose();
        }
      }
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('invoices.issueDialog.failed'));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open onClose={running ? undefined : onClose} fullWidth slotProps={{ paper: { sx: { maxWidth: 520, borderRadius: 4 } } }}>
      <DialogTitle sx={{ fontSize: 20, fontWeight: 600 }}>
        {monthLabel
          ? t('invoices.issueDialog.titleMonth', { count: drafts.length, month: monthLabel })
          : t('invoices.issueDialog.title', { count: drafts.length })}
      </DialogTitle>
      <DialogContent>
        {report ? (
          <Stack spacing={1.5}>
            {report.failedCount > 0 && (
              <>
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  {t('invoices.issueDialog.partial', { issued: report.issuedCount, failed: report.failedCount })}
                </Alert>
                <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 14 }}>
                  {report.outcomes
                    .filter(outcome => outcome.outcome === IssueOutcomeEnum.FAILED)
                    .map(outcome => (
                      <li key={outcome.invoiceId}>
                        {outcome.childFullName}: {outcome.reason}
                      </li>
                    ))}
                </Box>
              </>
            )}
            {report.outcomes.some(outcome => outcome.attendanceRecount) && (
              <>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {t('invoices.issueDialog.recounted', { issued: report.issuedCount })}
                </Alert>
                <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 14 }}>
                  {report.outcomes
                    .filter(outcome => outcome.attendanceRecount)
                    .map(outcome => (
                      <li key={outcome.invoiceId}>
                        {outcome.childFullName} ({outcome.invoiceNumber}):{' '}
                        {t('invoices.attendanceRecounted', { from: outcome.attendanceRecount!.from, to: outcome.attendanceRecount!.to })}
                      </li>
                    ))}
                </Box>
              </>
            )}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={2}>
              <GenericTextField label={t('invoices.issueDialog.issueDate')} isDate maxDate={null} value={issueDate} onChange={e => setIssueDate(e.target.value)} />
              <GenericTextField
                label={t('invoices.issueDialog.dueDate')}
                isDate
                maxDate={null}
                minDate={issueDate}
                value={dueDate}
                onChange={e => {
                  setDueTouched(true);
                  setDueDate(e.target.value);
                }}
                helperText={
                  dueTouched
                    ? t('invoices.issueDialog.dueForAll')
                    : dueDays !== null
                      ? t('invoices.issueDialog.dueHint', { count: dueDays })
                      : undefined
                }
              />
            </Stack>
            <Box>
              <FormControlLabel
                control={<Checkbox checked={send} onChange={event => setSend(event.target.checked)} />}
                label={<Typography sx={{ fontSize: 14 }}>{t('invoices.issueDialog.sendNow')}</Typography>}
              />
              {send && (
                <Typography sx={{ fontSize: 13, color: 'text.secondary', pl: 4 }}>
                  {t('invoices.issueDialog.withEmail', { withEmail, count: drafts.length })}
                </Typography>
              )}
            </Box>
            {zero > 0 && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('invoices.issueDialog.zeroIssuedPaid', { count: zero })}</Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {report ? (
          <GenericButton buttonType={GenericButtonTypeEnum.Primary} variant="contained" label={t('common.close')} onClick={onClose} />
        ) : (
          <>
            <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="outlined" label={t('common.cancel')} onClick={onClose} disabled={running} />
            <GenericButton
              buttonType={GenericButtonTypeEnum.Primary}
              variant="contained"
              label={t('invoices.issueDialog.submit', { count: drafts.length })}
              onAsyncAction={run}
              disabled={running || !drafts.length || !issueDate}
            />
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default IssueInvoicesDialog;
